/**
 * TeachBud Presentation Generator
 *
 * SYNCHRONIZED INSTRUCTIONAL RESOURCE CONTRACT
 * ─────────────────────────────────────────────
 * The Presentation is one of three coordinated views of the same LessonBlueprint.
 * Slides contain the EXACT studentContent and answer from the lesson.
 * Every practice slide is traceable to its blueprint item via blueprintRef.
 * Layout, pacing, and teaching moves may vary — content never changes.
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Monitor, Download, Play, Minimize2,
  CheckCircle2, XCircle, AlertCircle, Sparkles, Save, RotateCcw, RefreshCw,
  MessageSquare, Eye, EyeOff, Building2, BookOpen, Link,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useResources, type SavedResource } from './resources-context';
import { SaveStatusIndicator } from './SaveStatusIndicator';
import { useProfile } from './ProfileContext';
import type { GeneratedLesson } from './lesson-engine';
import { lessonContentKey, extractBlueprint, validateBlueprintCompleteness } from './lesson-blueprint';
import {
  defaultPresentationConfig,
  SLIDE_THEMES, SLIDE_KIND_LABELS,
  type PresentationConfig, type PresentationSlide, type GeneratedPresentation,
  type SlideTheme, type VisualStyle, type TeachingStyle, type QuestionStyle,
  type AnswerReveal, type EngagementAddon,
} from './presentation-engine';
import { generatePresentationAI, regenerateSlideAI, SubscriptionRequiredError } from './lesson-api';
import { runCertification, type CertificationReport } from './certification';
import { CertificationGate } from './CertificationGate';
import { type TeachBudThemeId, getTheme, THEME_LIST } from './theme-engine';
import { ThemePicker } from './ThemePicker';
import { useDefaultThemes } from './use-default-themes';
import { BrandingPanel } from './BrandingPanel';
import { defaultBranding, hasBranding, type BrandingData } from './branding';
import { GenerationProgress } from './GenerationProgress';
import { FlowDiagramSVG } from './FlowDiagramSVG';
import { TenFrameSVG, parseTenFrameHint } from './representations/TenFrameSVG';
import { NumberBondSVG, parseNumberBondHint } from './representations/NumberBondSVG';
import { FractionStripSVG, parseFractionStripHint } from './representations/FractionStripSVG';
import { NumberLineSVG, parseNumberLineHint } from './representations/NumberLineSVG';
import { SequenceChainSVG } from './representations/SequenceChainSVG';
import { VennDiagramSVG, parseVennDiagramHint } from './representations/VennDiagramSVG';
import { CERorganizerSVG } from './representations/CERorganizerSVG';
import { TChartSVG, parseTChartHint } from './representations/TChartSVG';
import { StoryMapSVG, parseStoryMapHint } from './representations/StoryMapSVG';
import { MainIdeaWebSVG } from './representations/MainIdeaWebSVG';
import { SoundBoxesSVG, parseSoundBoxesHint } from './representations/SoundBoxesSVG';
import { PictureLetterCardSVG, parsePictureLetterCardHint } from './representations/PictureLetterCardSVG';
import { resolveForHint, resolveWithFallback, REGISTRY_VERSION, gradeNumFromLabel, getGradeBandConfig } from './rendering-registry';
import { applyPrintBypass, addPptxBypassWatermark, type WatermarkableSlide } from './export-watermark';

// ── Toast ─────────────────────────────────────────────────────────────────────
interface Toast { id: number; message: string; type: 'success' | 'error' }
function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);
  function show(message: string, type: Toast['type'] = 'success') {
    const id = ++counter.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }
  return { toasts, show };
}
function ToastContainer({ toasts }: { toasts: Toast[] }) {
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] flex flex-col gap-2 pointer-events-none print:hidden">
      <AnimatePresence>
        {toasts.map(t => (
          <motion.div key={t.id}
            initial={{ opacity: 0, y: 16, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className={`flex items-center gap-2.5 px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold text-white ${t.type === 'success' ? 'bg-slate-900' : 'bg-red-600'}`}>
            {t.type === 'success' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
            {t.message}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ── Blueprint ref helpers ────────────────────────────────────────────────────
const SECTION_LABELS: Record<string, string> = {
  guidedPractice:      'Guided Practice',
  directInstruction:   'Direct Instruction',
  independentPractice: 'Independent Practice',
  warmUp:              'Warm-Up',
  exitTicket:          'Exit Ticket',
  vocabulary:          'Vocabulary',
  challenge:           'Challenge',
  homework:            'Homework',
};

function formatBlueprintRef(ref: PresentationSlide['blueprintRef']): string | null {
  if (!ref) return null;
  const label = SECTION_LABELS[ref.section] ?? ref.section.replace(/([A-Z])/g, ' $1').trim();
  return `${label} #${ref.n}`;
}

// ── Slide layout families ────────────────────────────────────────────────────
const KIND_ICONS: Record<string, string> = {
  'title': '🎯', 'learning-target': '📌', 'hook': '🪝', 'vocabulary': '📖',
  'concept': '💡', 'prediction': '🤔', 'discuss': '💬', 'reveal': '✅',
  'misconception': '⚠️', 'teacher-tip': '📢', 'practice': '✏️',
  'you-try': '🙋', 'engagement': '⚡', 'exit-ticket': '🚪',
};

type SlideFamily = 'hero' | 'hook' | 'vocab' | 'problem' | 'discuss' | 'reveal' | 'alert' | 'tip' | 'card';

function slideFamily(kind: string): SlideFamily {
  if (kind === 'title' || kind === 'learning-target') return 'hero';
  if (kind === 'hook' || kind === 'engagement') return 'hook';
  if (kind === 'vocabulary') return 'vocab';
  if (kind === 'practice' || kind === 'you-try') return 'problem';
  if (kind === 'discuss' || kind === 'prediction') return 'discuss';
  if (kind === 'reveal' || kind === 'exit-ticket') return 'reveal';
  if (kind === 'misconception') return 'alert';
  if (kind === 'teacher-tip') return 'tip';
  return 'card';
}

// ── Bullet list helper ────────────────────────────────────────────────────────
function BulletList({
  lines, theme, scale, dot,
}: {
  lines: string[]; theme: SlideTheme; scale: string; dot?: string;
}) {
  if (!lines.length) return null;
  return (
    <ul className={`space-y-1.5 ${scale}`}>
      {lines.map((line, i) => (
        <li key={i} className={`flex items-start gap-2 ${theme.body}`}>
          <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 flex-shrink-0 ${dot ?? theme.accent.replace('text-', 'bg-')}`} />
          <span className="leading-relaxed">{line}</span>
        </li>
      ))}
    </ul>
  );
}

// ── Slide canvas ──────────────────────────────────────────────────────────────
function SlideCanvas({
  slide, theme, fullscreen = false, showSpeakerNotes = false, revealHidden = false,
  afterDiscussion = false, discussionActive = false, gradeNum = 4,
}: {
  slide: PresentationSlide | undefined;
  theme: SlideTheme;
  fullscreen?: boolean;
  showSpeakerNotes?: boolean;
  revealHidden?: boolean;
  afterDiscussion?: boolean;
  discussionActive?: boolean;
  /** Numeric grade (0=K). Controls font scaling per spec §2: K-1 slides use larger text. */
  gradeNum?: number;
}) {
  if (!slide) return null;
  const isHidden = slide.isAnswerSlide && !revealHidden;
  const icon  = KIND_ICONS[slide.kind] ?? '📄';
  const label = SLIDE_KIND_LABELS[slide.kind as keyof typeof SLIDE_KIND_LABELS] ?? slide.kind;
  const FS    = fullscreen;
  const fam   = slideFamily(slide.kind);

  // Grade-band scale helpers (Spec §2: K-1 = larger text, more visual space)
  // K-1: bigger heading, bigger body — spec says K max 8 words/slide, G1 max 12.
  const isEarlyGrade = gradeNum <= 1;
  const h = FS ? (isEarlyGrade ? 'text-5xl' : 'text-4xl') : (isEarlyGrade ? 'text-2xl' : 'text-xl');
  const b = FS ? (isEarlyGrade ? 'text-2xl' : 'text-xl')  : (isEarlyGrade ? 'text-base' : 'text-sm');
  const s = FS ? (isEarlyGrade ? 'text-xl'  : 'text-lg')  : 'text-xs';
  const p = FS ? (isEarlyGrade ? 'p-14'     : 'p-12')     : 'p-7';
  // Image container is taller for K-1 (spec: visual-dominant, 60-70% of slide area)
  const imgMinH = FS
    ? (isEarlyGrade ? 'min-h-52' : 'min-h-36')
    : (isEarlyGrade ? 'min-h-20' : 'min-h-14');

  // Image / diagram element — routed through the rendering registry.
  // To add a new representation: add an entry in src/rendering-registry/registry.ts,
  // then add a case here. The registry's imageHintPattern routes the dispatch.
  const imgEl = !isHidden && (slide.imageUrl || slide.imageHint) ? (
    <div className={`${FS ? 'mt-6' : 'mt-3'} rounded-xl border border-current/10 flex items-center justify-center bg-white/10 overflow-hidden ${FS ? `${imgMinH} py-4 px-6` : `${imgMinH} py-2 px-3`}`}>
      {slide.imageUrl ? (
        <img src={slide.imageUrl} alt="" className="w-full h-full object-contain" />
      ) : slide.imageHint ? (() => {
        const entry = resolveForHint(slide.imageHint);
        switch (entry?.representation.type) {
          case 'ten-frame': {
            const { value, studentMode } = parseTenFrameHint(slide.imageHint);
            return <TenFrameSVG value={value} studentMode={studentMode} width={FS ? 360 : 220} showLabel />;
          }
          case 'number-bond': {
            const { whole, part1, part2 } = parseNumberBondHint(slide.imageHint);
            return <NumberBondSVG whole={whole} part1={part1} part2={part2} width={FS ? 300 : 180} />;
          }
          case 'fraction-strip': {
            const props = parseFractionStripHint(slide.imageHint);
            return <FractionStripSVG {...props} width={FS ? 480 : 280} />;
          }
          case 'number-line': {
            const props = parseNumberLineHint(slide.imageHint);
            return <NumberLineSVG {...props} width={FS ? 500 : 300} />;
          }
          case 'sequence-chain':
            return <SequenceChainSVG steps={4} studentMode={false} width={FS ? 560 : 320} />;
          case 'venn-diagram': {
            const props = parseVennDiagramHint(slide.imageHint);
            return <VennDiagramSVG {...props} width={FS ? 480 : 280} />;
          }
          case 'cer-organizer':
            return <CERorganizerSVG studentMode width={FS ? 480 : 280} />;
          case 't-chart': {
            const props = parseTChartHint(slide.imageHint);
            return <TChartSVG {...props} width={FS ? 480 : 280} />;
          }
          case 'story-map': {
            const props = parseStoryMapHint(slide.imageHint);
            return <StoryMapSVG {...props} studentMode width={FS ? 500 : 300} />;
          }
          case 'main-idea-web':
            return <MainIdeaWebSVG studentMode detailCount={3} width={FS ? 480 : 280} />;
          case 'sound-boxes': {
            const props = parseSoundBoxesHint(slide.imageHint);
            return <SoundBoxesSVG {...props} width={FS ? 380 : 220} />;
          }
          case 'picture-letter': {
            const props = parsePictureLetterCardHint(slide.imageHint);
            return <PictureLetterCardSVG {...props} width={FS ? 300 : 180} />;
          }
          default:
            /* flow-diagram, diagram, or any unrecognised hint — FlowDiagramSVG */
            return <FlowDiagramSVG imageHint={slide.imageHint} compact={!FS} className="w-full" />;
        }
      })() : null}
    </div>
  ) : null;

  // Hidden overlay
  const overlay = isHidden && (
    <div className="absolute inset-0 flex items-center justify-center z-10 rounded-xl bg-black/62">
      {afterDiscussion && !discussionActive
        ? (
          <div className="text-center text-white px-6">
            <div className={`mb-3 ${FS ? 'text-6xl' : 'text-3xl'}`}>🗣️</div>
            <p className={`font-bold ${FS ? 'text-2xl' : 'text-base'}`}>Let students discuss first</p>
            <p className={`opacity-70 mt-1 ${FS ? 'text-lg' : 'text-xs'}`}>Press → to enter discussion mode</p>
          </div>
        )
        : afterDiscussion && discussionActive
        ? (
          <div className="text-center text-white px-6">
            <div className={`mb-3 ${FS ? 'text-6xl' : 'text-3xl'}`}>💬</div>
            <p className={`font-bold ${FS ? 'text-2xl' : 'text-base'}`}>Students are discussing</p>
            <p className={`opacity-70 mt-1 ${FS ? 'text-lg' : 'text-xs'}`}>Press R or click Reveal to show answer</p>
          </div>
        )
        : (
          <div className="text-center text-white">
            <Eye size={FS ? 48 : 24} className="mx-auto mb-3 opacity-60" />
            <p className={`font-bold ${FS ? 'text-2xl' : 'text-sm'}`}>Answer hidden</p>
            <p className={`opacity-60 mt-1 ${FS ? 'text-lg' : 'text-xs'}`}>Click reveal to show</p>
          </div>
        )
      }
    </div>
  );

  // ── Per-kind content ──────────────────────────────────────────────────────
  let content: React.ReactNode;

  if (fam === 'hero') {
    content = (
      <div className={`flex-1 flex flex-col justify-center items-center text-center ${p}`}>
        <div className={`mb-4 ${FS ? 'text-6xl' : 'text-3xl'}`}>{icon}</div>
        <h1 className={`font-black leading-tight ${FS ? 'text-5xl' : 'text-3xl'} ${theme.heading}`}>
          {slide.heading}
        </h1>
        {slide.subheading && <p className={`mt-4 opacity-70 ${s} ${theme.body}`}>{slide.subheading}</p>}
        {slide.bodyLines.length > 0 && (
          <div className="mt-6 max-w-xl text-left">
            <BulletList lines={slide.bodyLines} theme={theme} scale={b} />
          </div>
        )}
      </div>
    );
  } else if (fam === 'hook') {
    content = (
      <div className={`flex-1 flex flex-col justify-center items-center text-center ${p}`}>
        <div className={`mb-5 ${FS ? 'text-5xl' : 'text-2xl'}`}>{icon}</div>
        <blockquote className={`font-black italic leading-tight ${FS ? 'text-5xl' : 'text-2xl'} ${theme.heading}`}>
          "{slide.heading}"
        </blockquote>
        {slide.bodyLines.length > 0 && (
          <div className="mt-6 max-w-xl text-left">
            <BulletList lines={slide.bodyLines} theme={theme} scale={b} />
          </div>
        )}
      </div>
    );
  } else if (fam === 'vocab') {
    content = (
      <div className="flex-1 flex overflow-hidden">
        {/* Term column */}
        <div className={`w-2/5 flex items-center justify-center ${p} ${theme.accentBg}`}>
          <h2 className={`font-black text-center leading-tight ${h} ${theme.accent}`}>{slide.heading}</h2>
        </div>
        {/* Definition column */}
        <div className={`flex-1 flex flex-col justify-center ${p} border-l ${theme.border}`}>
          {slide.subheading && <p className={`font-bold mb-3 ${s} ${theme.heading}`}>{slide.subheading}</p>}
          <BulletList lines={slide.bodyLines} theme={theme} scale={b} />
          {imgEl}
        </div>
      </div>
    );
  } else if (fam === 'problem') {
    content = (
      <div className={`flex-1 flex flex-col justify-center ${p} gap-4`}>
        <div className="flex items-center gap-2">
          <span className={FS ? 'text-3xl' : 'text-xl'}>{icon}</span>
          <span className={`font-bold uppercase tracking-widest ${FS ? 'text-sm' : 'text-[10px]'} ${theme.accent}`}>
            {slide.kind === 'you-try' ? 'Your Turn' : 'Try It'}
          </span>
        </div>
        <div className={`rounded-2xl border-2 ${theme.border} bg-white/20 ${FS ? 'p-8' : 'p-4'}`}>
          <h2 className={`font-bold leading-snug ${h} ${theme.heading}`}>{slide.heading}</h2>
          {slide.subheading && <p className={`mt-2 opacity-70 ${s} ${theme.body}`}>{slide.subheading}</p>}
          {slide.bodyLines.length > 0 && (
            <div className="mt-3">
              <BulletList lines={slide.bodyLines} theme={theme} scale={b} />
            </div>
          )}
        </div>
        {imgEl}
      </div>
    );
  } else if (fam === 'discuss') {
    content = (
      <div className={`flex-1 flex flex-col justify-center ${p} gap-4`}>
        <div className="flex items-center gap-2">
          <span className={FS ? 'text-3xl' : 'text-xl'}>💬</span>
          <span className={`font-bold uppercase tracking-widest ${FS ? 'text-sm' : 'text-[10px]'} ${theme.accent}`}>
            {slide.kind === 'prediction' ? 'Predict' : 'Discuss'}
          </span>
        </div>
        <div className={`rounded-2xl border-2 border-dashed ${theme.border} bg-white/10 ${FS ? 'p-8' : 'p-4'}`}>
          <h2 className={`font-bold leading-snug ${h} ${theme.heading}`}>{slide.heading}</h2>
          {slide.subheading && <p className={`mt-2 opacity-70 ${s} ${theme.body}`}>{slide.subheading}</p>}
          {slide.bodyLines.length > 0 && (
            <div className="mt-3">
              <BulletList lines={slide.bodyLines} theme={theme} scale={b} />
            </div>
          )}
        </div>
        {!FS && <p className={`${theme.body} opacity-40 ${s} italic`}>Turn and talk with a partner</p>}
      </div>
    );
  } else if (fam === 'reveal') {
    content = (
      <div className={`flex-1 flex flex-col justify-center ${p} gap-4`}>
        <div className="flex items-center gap-2">
          <span className={FS ? 'text-3xl' : 'text-xl'}>✅</span>
          <span className={`font-bold uppercase tracking-widest ${FS ? 'text-sm' : 'text-[10px]'} text-emerald-600`}>
            {slide.kind === 'exit-ticket' ? 'Exit Ticket' : 'Answer'}
          </span>
        </div>
        <div className={`rounded-2xl border-2 border-emerald-300/70 bg-emerald-50/40 ${FS ? 'p-8' : 'p-4'}`}>
          <h2 className={`font-bold leading-snug ${h} ${theme.heading}`}>{slide.heading}</h2>
          {slide.subheading && <p className={`mt-2 opacity-70 ${s} ${theme.body}`}>{slide.subheading}</p>}
          {slide.bodyLines.length > 0 && (
            <div className="mt-3">
              <BulletList lines={slide.bodyLines} theme={theme} scale={b} dot="bg-emerald-500" />
            </div>
          )}
        </div>
        {imgEl}
      </div>
    );
  } else if (fam === 'alert') {
    content = (
      <div className={`flex-1 flex flex-col justify-center ${p} gap-4`}>
        <div className="flex items-center gap-2">
          <span className={FS ? 'text-3xl' : 'text-xl'}>⚠️</span>
          <span className={`font-bold uppercase tracking-widest ${FS ? 'text-sm' : 'text-[10px]'} text-amber-600`}>
            Common Mistake
          </span>
        </div>
        <div className={`rounded-2xl border-2 border-amber-300/70 bg-amber-50/40 ${FS ? 'p-8' : 'p-4'}`}>
          <h2 className={`font-bold leading-snug ${h} ${theme.heading}`}>{slide.heading}</h2>
          {slide.bodyLines.length > 0 && (
            <div className="mt-3">
              <BulletList lines={slide.bodyLines} theme={theme} scale={b} dot="bg-amber-500" />
            </div>
          )}
        </div>
      </div>
    );
  } else if (fam === 'tip') {
    content = (
      <div className={`flex-1 flex flex-col justify-center ${p} gap-4`}>
        <div className="flex items-center gap-2">
          <span className={FS ? 'text-3xl' : 'text-xl'}>📢</span>
          <span className={`font-bold uppercase tracking-widest ${FS ? 'text-sm' : 'text-[10px]'} text-blue-600`}>
            Teacher Note
          </span>
        </div>
        <div className={`rounded-2xl border-2 border-blue-200/70 bg-blue-50/30 ${FS ? 'p-8' : 'p-4'}`}>
          <h2 className={`font-bold leading-snug ${h} ${theme.heading}`}>{slide.heading}</h2>
          {slide.bodyLines.length > 0 && (
            <div className="mt-3">
              <BulletList lines={slide.bodyLines} theme={theme} scale={b} dot="bg-blue-400" />
            </div>
          )}
        </div>
      </div>
    );
  } else {
    // card / concept / standard fallback
    content = (
      <div className={`flex-1 flex flex-col justify-center ${p}`}>
        <div className="flex items-center gap-2 mb-4">
          <span className={FS ? 'text-2xl' : 'text-lg'}>{icon}</span>
          <span className={`font-bold uppercase tracking-widest ${FS ? 'text-xs' : 'text-[10px]'} ${theme.accent}`}>{label}</span>
        </div>
        <h2 className={`font-bold leading-tight mb-4 ${h} ${theme.heading}`}>{slide.heading}</h2>
        {slide.subheading && <p className={`mb-4 opacity-70 ${s} ${theme.body}`}>{slide.subheading}</p>}
        <BulletList lines={slide.bodyLines} theme={theme} scale={b} />
        {imgEl}
        {!fullscreen && (slide.teachingMove || slide.blueprintRef) && (
          <div className="mt-5 flex flex-wrap gap-2">
            {slide.teachingMove && (
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${theme.accentBg} ${theme.accent}`}>
                <MessageSquare size={11} />
                {slide.teachingMove.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
            )}
            {slide.blueprintRef && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-white/20 opacity-60 border border-current/20">
                🔗 {formatBlueprintRef(slide.blueprintRef)}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative w-full h-full ${theme.bg} flex flex-col rounded-xl overflow-hidden`}>
      {overlay}
      {content}
      {/* Teaching-move + blueprint badges for families that don't include them in content */}
      {!fullscreen && fam !== 'card' && fam !== 'vocab' && (slide.teachingMove || slide.blueprintRef) && (
        <div className="px-7 pb-3 flex flex-wrap gap-2 flex-shrink-0">
          {slide.teachingMove && (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${theme.accentBg} ${theme.accent}`}>
              <MessageSquare size={11} />
              {slide.teachingMove.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
          )}
          {slide.blueprintRef && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-white/20 opacity-60 border border-current/20">
              🔗 {formatBlueprintRef(slide.blueprintRef)}
            </span>
          )}
        </div>
      )}
      {/* Speaker notes strip */}
      {showSpeakerNotes && slide.speakerNotes && (
        <div className={`border-t ${theme.border} px-7 py-3 bg-black/10 flex-shrink-0`}>
          <p className={`${s} ${theme.body} opacity-80 italic`}>
            <span className="font-semibold not-italic">📝 </span>{slide.speakerNotes}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Slide thumbnail ───────────────────────────────────────────────────────────
function SlideThumbnail({
  slide, index, active, onClick, theme, isHidden, onToggleHidden,
}: {
  slide: PresentationSlide; index: number; active: boolean;
  onClick: () => void; theme: SlideTheme;
  isHidden?: boolean; onToggleHidden?: () => void;
}) {
  return (
    <div className="relative group">
      <button onClick={onClick}
        className={`w-full flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all text-left ${isHidden ? 'opacity-40' : ''} ${active ? 'border-primary shadow-md shadow-primary/20' : 'border-transparent hover:border-slate-300'}`}>
        <div className={`w-full aspect-video ${theme.bg} p-2 flex flex-col justify-center`}>
          <p className={`text-[8px] font-bold leading-tight truncate ${theme.heading}`}>{slide.heading}</p>
          {slide.bodyLines.slice(0, 2).map((line, i) => (
            <p key={i} className={`text-[6px] mt-0.5 truncate opacity-60 ${theme.body}`}>
              {typeof line === 'string' ? line : String(line ?? '')}
            </p>
          ))}
        </div>
        <div className="bg-white border-t border-slate-100 px-1.5 py-1 flex items-center gap-1">
          <span className={`text-[9px] font-bold rounded-full px-1.5 py-0.5 ${active ? 'bg-primary text-white' : 'bg-slate-100 text-slate-500'}`}>{index + 1}</span>
          <span className="text-[9px] text-slate-400 truncate">
            {slide.blueprintRef
              ? formatBlueprintRef(slide.blueprintRef)
              : (SLIDE_KIND_LABELS[slide.kind as keyof typeof SLIDE_KIND_LABELS] ?? slide.kind)}
          </span>
        </div>
      </button>
      {onToggleHidden && (
        <button
          onClick={e => { e.stopPropagation(); onToggleHidden(); }}
          title={isHidden ? 'Show slide in presentation' : 'Hide slide from presentation'}
          className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 p-0.5 bg-white/90 rounded transition-opacity shadow-sm">
          {isHidden ? <EyeOff size={10} className="text-slate-600" /> : <Eye size={10} className="text-slate-600" />}
        </button>
      )}
    </div>
  );
}

// ── Regenerate slide dialog ───────────────────────────────────────────────────
const REGEN_OPTIONS = [
  'More colorful', 'Simpler wording', 'Add a diagram hint', 'Fewer words',
  'More student interaction', 'Add a teaching tip', 'Make it more visual',
];

function RegenerateDialog({
  onConfirm, onCancel, loading,
}: { onConfirm: (prefs: string[]) => void; onCancel: () => void; loading: boolean }) {
  const [selected, setSelected] = useState<string[]>([]);
  const [custom, setCustom] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full z-10">
        <p className="text-slate-800 font-bold text-lg mb-1 flex items-center gap-2">
          <RefreshCw size={16} className="text-primary" /> Regenerate Slide
        </p>
        <p className="text-slate-500 text-sm mb-4">Select preferences (or leave blank for a general refresh):</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {REGEN_OPTIONS.map(opt => (
            <button key={opt}
              onClick={() => setSelected(prev => prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt])}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${selected.includes(opt) ? 'border-primary bg-primary/10 text-primary' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
              {opt}
            </button>
          ))}
        </div>
        <input
          type="text" placeholder="Custom preference…" value={custom}
          onChange={e => setCustom(e.target.value)}
          className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 mb-4 outline-none focus:border-primary"
        />
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 rounded-xl border-slate-200" onClick={onCancel} disabled={loading}>Cancel</Button>
          <Button className="flex-1 rounded-xl bg-primary hover:bg-primary/90 text-white"
            onClick={() => onConfirm([...selected, ...(custom.trim() ? [custom.trim()] : [])])}
            disabled={loading}>
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                Regenerating…
              </span>
            ) : 'Regenerate'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Config view ────────────────────────────────────────────────────────────────
const VISUAL_STYLE_OPTIONS: { value: VisualStyle; label: string; preview: string }[] = [
  { value: 'minimal',       label: 'Minimal',       preview: 'bg-white border border-slate-200' },
  { value: 'modern',        label: 'Modern',        preview: 'bg-white border-2 border-blue-500' },
  { value: 'elementary',    label: 'Elementary',    preview: 'bg-amber-50 border-2 border-orange-400' },
  { value: 'middle-school', label: 'Middle School', preview: 'bg-violet-50 border-2 border-violet-500' },
  { value: 'high-school',   label: 'High School',   preview: 'bg-slate-50 border-2 border-indigo-500' },
  { value: 'colorful',      label: 'Colorful',      preview: 'bg-gradient-to-br from-blue-500 to-violet-600' },
  { value: 'dark',          label: 'Dark',          preview: 'bg-slate-900 border border-slate-700' },
];
const TEACHING_STYLE_OPTIONS: { value: TeachingStyle; label: string; desc: string }[] = [
  { value: 'direct-instruction', label: 'Direct Instruction', desc: 'I Do → We Do → You Do' },
  { value: 'inquiry',            label: 'Inquiry',            desc: 'Question → Explore → Reveal' },
  { value: 'gradual-release',    label: 'Gradual Release',    desc: 'Model → Guided → Independent' },
  { value: 'workshop',           label: 'Workshop',           desc: 'Mini-lesson + Work time' },
  { value: 'interactive',        label: 'Interactive',        desc: 'Maximum student voice' },
];
const QUESTION_STYLE_OPTIONS: { value: QuestionStyle; label: string }[] = [
  { value: 'original',            label: 'Original (as written)' },
  { value: 'think-pair-share',    label: 'Think-Pair-Share' },
  { value: 'turn-and-talk',       label: 'Turn and Talk' },
  { value: 'cold-call',           label: 'Cold Call' },
  { value: 'poll',                label: 'Poll / Vote' },
  { value: 'whiteboard-response', label: 'Whiteboard Response' },
  { value: 'multiple-choice',     label: 'Convert to Multiple Choice' },
];
const ANSWER_REVEAL_OPTIONS: { value: AnswerReveal; label: string; desc: string }[] = [
  { value: 'immediately',        label: 'Immediately',        desc: 'Show answer as soon as slide appears' },
  { value: 'after-discussion',   label: 'After Discussion',   desc: 'Two-phase: discuss first, then reveal' },
  { value: 'hidden-until-click', label: 'Hidden Until Click', desc: 'Click reveal button to show' },
  { value: 'teacher-only',       label: 'Teacher Only',       desc: 'Never shown to students' },
];
const ENGAGEMENT_ADDON_OPTIONS: { value: EngagementAddon; label: string }[] = [
  { value: 'think-pair-share',  label: 'Think Pair Share' },
  { value: 'discussion-prompt', label: 'Discussion Prompt' },
  { value: 'challenge-question',label: 'Challenge Question' },
  { value: 'brain-break',       label: 'Brain Break' },
  { value: 'partner-activity',  label: 'Partner Activity' },
  { value: 'poll',              label: 'Poll' },
  { value: 'mini-quiz',         label: 'Mini Quiz' },
  { value: 'exit-reflection',   label: 'Exit Reflection' },
];

function ConfigView({
  lesson, config, setConfig, onGenerate, generating, onBack, error, themeId, onThemeChange,
}: {
  lesson: GeneratedLesson; config: PresentationConfig;
  setConfig: React.Dispatch<React.SetStateAction<PresentationConfig>>;
  onGenerate: () => void; generating: boolean; onBack: () => void; error: string | null;
  themeId: TeachBudThemeId; onThemeChange: (id: TeachBudThemeId) => void;
}) {
  function set<K extends keyof PresentationConfig>(key: K, value: PresentationConfig[K]) {
    setConfig(prev => ({ ...prev, [key]: value }));
  }
  function toggleAddon(addon: EngagementAddon) {
    setConfig(prev => ({
      ...prev,
      engagementAddons: prev.engagementAddons.includes(addon)
        ? prev.engagementAddons.filter(a => a !== addon)
        : [...prev.engagementAddons, addon],
    }));
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <button onClick={onBack}
        className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors px-3 py-2 rounded-xl hover:bg-slate-100">
        <ChevronLeft size={15} /> Back to Lesson
      </button>

      <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-5 text-white shadow-lg">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Monitor size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold">Generate Presentation</h2>
            <p className="text-amber-100 text-sm leading-snug mt-0.5">
              Creating from: <span className="font-semibold text-white">{lesson.title}</span>
            </p>
            <p className="text-amber-200 text-xs mt-1">{lesson.gradeLabel} · {lesson.subject}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2.5 text-sm text-red-700">
          <AlertCircle size={15} className="flex-shrink-0" /> {error}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-6">
        {/* Visual style / theme */}
        <ThemePicker
          value={themeId}
          onChange={id => {
            onThemeChange(id);
            const TV: Record<string, VisualStyle> = {
              clean: 'minimal', modern: 'modern', elementary: 'elementary',
              'middle-school': 'middle-school', 'high-school': 'high-school',
              'state-test': 'minimal', notebook: 'modern', worksheet: 'minimal',
              chalkboard: 'dark', contemporary: 'modern', fun: 'colorful', elegant: 'modern',
            };
            set('visualStyle', (TV[id] ?? 'modern') as VisualStyle);
          }}
          label="Visual Theme"
        />

        {/* Graphics density */}
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-1">Graphics Density</p>
          <p className="text-xs text-slate-500 mb-2.5">Adds diagram hints to speaker notes. Never changes problems or scenarios.</p>
          <div className="flex gap-2 flex-wrap">
            {([['none','None'],['minimal','Minimal'],['standard','Standard'],['rich','Rich']] as const).map(([val, label]) => (
              <button key={val}
                onClick={() => setConfig(prev => ({ ...prev, graphicsDensity: val }))}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${config.graphicsDensity === val ? 'bg-amber-100 border-amber-400 text-amber-800' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Teaching style + Question style */}
        <div className="grid grid-cols-2 gap-5">
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-2.5">Teaching Style</p>
            <div className="space-y-1.5">
              {TEACHING_STYLE_OPTIONS.map(opt => (
                <label key={opt.value}
                  className={`flex items-start gap-2.5 px-3 py-2 rounded-xl border cursor-pointer transition-all ${config.teachingStyle === opt.value ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" name="teachingStyle" value={opt.value} checked={config.teachingStyle === opt.value}
                    onChange={() => set('teachingStyle', opt.value)} className="sr-only" />
                  <div className={`mt-1 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${config.teachingStyle === opt.value ? 'border-amber-500' : 'border-slate-300'}`}>
                    {config.teachingStyle === opt.value && <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                  </div>
                  <div>
                    <span className={`text-sm font-medium ${config.teachingStyle === opt.value ? 'text-amber-800' : 'text-slate-700'}`}>{opt.label}</span>
                    <span className="block text-xs text-slate-400">{opt.desc}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-2.5">Question Style</p>
            <div className="space-y-1.5">
              {QUESTION_STYLE_OPTIONS.map(opt => (
                <label key={opt.value}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border cursor-pointer transition-all ${config.questionStyle === opt.value ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" name="questionStyle" value={opt.value} checked={config.questionStyle === opt.value}
                    onChange={() => set('questionStyle', opt.value)} className="sr-only" />
                  <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${config.questionStyle === opt.value ? 'border-blue-500' : 'border-slate-300'}`}>
                    {config.questionStyle === opt.value && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                  </div>
                  <span className={`text-sm font-medium ${config.questionStyle === opt.value ? 'text-blue-800' : 'text-slate-600'}`}>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Answer reveal */}
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2.5">Answer Reveal</p>
          <div className="grid grid-cols-2 gap-2">
            {ANSWER_REVEAL_OPTIONS.map(opt => (
              <label key={opt.value}
                className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${config.answerReveal === opt.value ? 'border-violet-400 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}>
                <input type="radio" name="answerReveal" value={opt.value} checked={config.answerReveal === opt.value}
                  onChange={() => set('answerReveal', opt.value)} className="sr-only" />
                <div className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${config.answerReveal === opt.value ? 'border-violet-500' : 'border-slate-300'}`}>
                  {config.answerReveal === opt.value && <div className="w-1.5 h-1.5 rounded-full bg-violet-500" />}
                </div>
                <div>
                  <span className={`text-sm font-semibold ${config.answerReveal === opt.value ? 'text-violet-800' : 'text-slate-700'}`}>{opt.label}</span>
                  <span className="block text-xs text-slate-400">{opt.desc}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Engagement add-ons */}
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-2.5">Engagement Add-ons</p>
          <div className="grid grid-cols-2 gap-2">
            {ENGAGEMENT_ADDON_OPTIONS.map(opt => {
              const checked = config.engagementAddons.includes(opt.value);
              return (
                <label key={opt.value}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border cursor-pointer transition-all ${checked ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${checked ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}
                    onClick={() => toggleAddon(opt.value)}>
                    {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <span className={`text-sm font-medium ${checked ? 'text-emerald-800' : 'text-slate-600'}`}>{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      <Button onClick={onGenerate} disabled={generating}
        className="w-full h-12 rounded-xl text-base font-semibold bg-amber-500 hover:bg-amber-600 text-white shadow-sm">
        {generating ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            Building Presentation…
          </span>
        ) : (
          <span className="flex items-center gap-2"><Sparkles size={17} /> Generate Presentation</span>
        )}
      </Button>
    </div>
  );
}

// ── Result view ────────────────────────────────────────────────────────────────
function ResultView({
  presentation, lesson, onBack, onNew, onSave, savedResourceId,
  themeId, onThemeChange,
}: {
  presentation: GeneratedPresentation;
  lesson: GeneratedLesson;
  onBack: () => void;
  onNew: () => void;
  onSave: (updated: GeneratedPresentation) => void;
  savedResourceId: string | null;
  themeId: TeachBudThemeId;
  onThemeChange: (id: TeachBudThemeId) => void;
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showNotes, setShowNotes]       = useState(false);
  const [showFullscreenNotes, setShowFullscreenNotes] = useState(false);
  const [revealCurrent, setRevealCurrent] = useState(false);
  const [regenOpen, setRegenOpen]       = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [slides, setSlides]             = useState(presentation.slides);
  const [slideEditMode, setSlideEditMode] = useState(false);

  // Initialize hiddenSlides and branding from persisted presentation data
  const [hiddenSlides, setHiddenSlides] = useState<string[]>(presentation.hiddenSlides ?? []);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const [certReport, setCertReport] = useState<CertificationReport | null>(null);
  const [pendingExportAction, setPendingExportAction] = useState<(() => void) | null>(null);

  const [branding, setBranding]         = useState<BrandingData>(() => {
    if (presentation.branding && Object.keys(presentation.branding).length) {
      return presentation.branding as unknown as BrandingData;
    }
    return defaultBranding();
  });
  const [showBrandingPanel, setShowBrandingPanel] = useState(false);

  // Discussion phase: Set of answer-slide IDs that are in "active discussion" state
  // after-discussion mode: first → enters discussion, second → (or R) reveals
  const [discussionPhase, setDiscussionPhase] = useState<Set<string>>(new Set());

  const presenterRef = useRef<HTMLDivElement>(null);

  // Refs so keyboard handler always sees current state without stale closures
  const hiddenSlidesRef    = useRef(hiddenSlides);
  const slidesRef          = useRef(slides);
  const currentSlideRef    = useRef(currentSlide);
  const revealCurrentRef   = useRef(revealCurrent);
  const discussionPhaseRef = useRef(discussionPhase);
  useEffect(() => { hiddenSlidesRef.current    = hiddenSlides;    }, [hiddenSlides]);
  useEffect(() => { slidesRef.current          = slides;          }, [slides]);
  useEffect(() => { currentSlideRef.current    = currentSlide;    }, [currentSlide]);
  useEffect(() => { revealCurrentRef.current   = revealCurrent;   }, [revealCurrent]);
  useEffect(() => { discussionPhaseRef.current = discussionPhase; }, [discussionPhase]);

  // Close export menu when clicking outside
  useEffect(() => {
    if (!showExportMenu) return;
    function handleOutside(e: MouseEvent) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showExportMenu]);

  const { show: showToast, toasts } = useToasts();

  const theme = getTheme(themeId).slide ?? SLIDE_THEMES[presentation.config.visualStyle];
  const gradeNum = gradeNumFromLabel(presentation.gradeLabel);
  const slide = slides[currentSlide];
  const total = slides.length;

  const autoReveal     = presentation.config.answerReveal === 'immediately';
  const teacherOnly    = presentation.config.answerReveal === 'teacher-only';
  const afterDiscMode  = presentation.config.answerReveal === 'after-discussion';
  const effectiveReveal = autoReveal || (revealCurrent && !teacherOnly);

  // Visible slides for accurate counter
  const visibleSlides  = slides.filter(s => !hiddenSlides.includes(s.id));
  const visibleIdx     = visibleSlides.findIndex(s => s.id === slides[currentSlide]?.id);
  const visiblePos     = visibleIdx >= 0 ? visibleIdx + 1 : 0;
  const visibleTotal   = visibleSlides.length;

  // ── Slide text editing helpers ───────────────────────────────────────────
  function updateCurrentSlide(patch: Partial<PresentationSlide>) {
    setSlides(prev => prev.map((s, i) => i === currentSlide ? { ...s, ...patch } : s));
  }
  function updateBodyLine(lineIdx: number, val: string) {
    updateCurrentSlide({ bodyLines: slide.bodyLines.map((l, i) => i === lineIdx ? val : l) });
  }
  function addBodyLine()              { updateCurrentSlide({ bodyLines: [...slide.bodyLines, ''] }); }
  function removeBodyLine(i: number)  { updateCurrentSlide({ bodyLines: slide.bodyLines.filter((_, j) => j !== i) }); }

  // ── Navigation helpers ───────────────────────────────────────────────────
  function nextVisible(from: number, dir: 1 | -1): number {
    const sl = slidesRef.current;
    const hs = hiddenSlidesRef.current;
    let idx = from + dir;
    while (idx >= 0 && idx < sl.length) {
      if (!hs.includes(sl[idx].id)) return idx;
      idx += dir;
    }
    return from;
  }

  function goToSlide(n: number) {
    setCurrentSlide(n);
    setRevealCurrent(false);
  }

  // ── Keyboard navigation ──────────────────────────────────────────────────
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      // S key: toggle speaker notes in fullscreen
      if ((e.key === 's' || e.key === 'S') && isFullscreen) {
        setShowFullscreenNotes(v => !v);
        return;
      }
      // R key: reveal current answer
      if (e.key === 'r' || e.key === 'R') {
        const curr = slidesRef.current[currentSlideRef.current];
        if (curr?.isAnswerSlide) {
          if (afterDiscMode && !discussionPhaseRef.current.has(curr.id)) {
            // First R in after-discussion: enter discussion phase
            setDiscussionPhase(prev => new Set([...prev, curr.id]));
          } else {
            setRevealCurrent(true);
          }
        }
        return;
      }
      // Forward: ArrowRight / ArrowDown / Space
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        const curr = slidesRef.current[currentSlideRef.current];
        const isHiddenAnswer = curr?.isAnswerSlide && !revealCurrentRef.current && !autoReveal && !teacherOnly;
        if (isHiddenAnswer && afterDiscMode) {
          if (!discussionPhaseRef.current.has(curr.id)) {
            // Phase 1: enter discussion
            setDiscussionPhase(prev => new Set([...prev, curr.id]));
          } else {
            // Phase 2: reveal
            setRevealCurrent(true);
          }
        } else {
          setCurrentSlide(prev => nextVisible(prev, 1));
          setRevealCurrent(false);
        }
        return;
      }
      // Back: ArrowLeft / ArrowUp
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setCurrentSlide(prev => nextVisible(prev, -1));
        setRevealCurrent(false);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, isFullscreen, autoReveal, teacherOnly, afterDiscMode]);

  // Fullscreen change detection
  useEffect(() => {
    function onFSChange() { if (!document.fullscreenElement) setIsFullscreen(false); }
    document.addEventListener('fullscreenchange', onFSChange);
    return () => document.removeEventListener('fullscreenchange', onFSChange);
  }, []);

  async function enterFullscreen() {
    try {
      if (presenterRef.current) {
        await presenterRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } catch { /* denied or unsupported */ }
  }
  async function exitFullscreen() {
    try { await document.exitFullscreen(); } catch { /* ignore */ }
    setIsFullscreen(false);
  }

  async function handleRegenerate(prefs: string[]) {
    setRegenLoading(true);
    try {
      const updated = await regenerateSlideAI(lesson, slide, prefs);
      setSlides(prev => prev.map((s, i) => i === currentSlide ? updated : s));
      setRegenOpen(false);
      showToast('Slide regenerated.', 'success');
    } catch {
      showToast('Regeneration failed. Please try again.', 'error');
    } finally {
      setRegenLoading(false);
    }
  }

  // ── Save: merge local state back into presentation ───────────────────────
  function buildUpdatedPresentation(): GeneratedPresentation {
    // Persist the teacher's chosen theme (received as the `themeId` prop from the outer
    // container) in lessonData so it survives close/reopen.
    return {
      ...presentation,
      slides,
      hiddenSlides,
      branding: branding as unknown as Record<string, unknown>,
      themeId,
    } as GeneratedPresentation;
  }

  function handleSaveClick() {
    onSave(buildUpdatedPresentation());
  }

  // ── PPTX export ──────────────────────────────────────────────────────────

  function runExportCertification(kind: 'pptx' | 'print') {
    const visibleSlidesForCheck = slides.filter(s => !hiddenSlides.includes(s.id));
    const blueprint = lesson.blueprint ?? extractBlueprint(lesson);
    const report = runCertification({
      resource: 'presentation',
      presentation,
      visibleSlides: visibleSlidesForCheck,
      lesson,
      blueprint,
    });
    if (!report.passed || report.hasWarnings) {
      setCertReport(report);
      setPendingExportAction(() => () => {
        setCertReport(null);
        setPendingExportAction(null);
        if (kind === 'pptx') void doExportPPTX(true); // bypass — watermark on every slide
        else applyPrintBypass(() => window.print(), true); // bypass — watermark on every page
      });
      return true; // halted — cert modal shown
    }
    return false; // all clear — proceed immediately
  }

  async function doExportPPTX(isBypass = false) {
    showToast('Preparing PPTX download…', 'success');
    try {
      const pptxgenModule = await import('pptxgenjs');
      const pptxgen = pptxgenModule.default;
      const prs = new pptxgen();
      const t = theme;

      const brandingFooter = hasBranding(branding) ? [
        branding.visibleFields.schoolName  && branding.schoolName,
        branding.visibleFields.teacherName && branding.teacherName,
        branding.visibleFields.courseClass && branding.courseClass,
        branding.visibleFields.period      && branding.period ? `Period ${branding.period}` : null,
        branding.visibleFields.schoolYear  && branding.schoolYear,
      ].filter(Boolean).join('  ·  ') : '';

      const visibleForExport = slides.filter(s => !hiddenSlides.includes(s.id));

      for (const s of visibleForExport) {
        const sl  = prs.addSlide();
        const fam = slideFamily(s.kind);
        sl.background = { color: t.pptxBg };

        // ── Per-kind layout ───────────────────────────────────────────────
        if (fam === 'hero') {
          // Large centered title
          sl.addText(s.heading, {
            x: 0.5, y: 1.8, w: 9, h: 2.8,
            fontSize: s.heading.length > 50 ? 36 : 44,
            bold: true, color: t.pptxHeading,
            align: 'center', valign: 'middle', wrap: true,
          });
          if (s.subheading) {
            sl.addText(s.subheading, {
              x: 1, y: 4.8, w: 8, h: 0.6,
              fontSize: 20, color: t.pptxBody, align: 'center', italic: true,
            });
          }
          if (s.bodyLines.length) {
            sl.addText(s.bodyLines.map(l => ({ text: `• ${l}`, options: {} })), {
              x: 1.5, y: 5.5, w: 7, h: 1.8, fontSize: 16, color: t.pptxBody, wrap: true, align: 'center',
            });
          }
        } else if (fam === 'hook') {
          // Italic quote style
          sl.addText(`"${s.heading}"`, {
            x: 0.8, y: 1.5, w: 8.4, h: 4,
            fontSize: s.heading.length > 60 ? 28 : 38,
            bold: true, italic: true, color: t.pptxHeading,
            align: 'center', valign: 'middle', wrap: true,
          });
        } else if (fam === 'vocab') {
          // Left accent block = term, right = definition
          sl.addShape(prs.ShapeType.rect, { x: 0, y: 0, w: 3.5, h: 7.5, fill: { color: t.pptxHeading + '18' } });
          sl.addText(s.heading, {
            x: 0.2, y: 0.5, w: 3.1, h: 6.5,
            fontSize: s.heading.length > 15 ? 28 : 36,
            bold: true, color: t.pptxHeading, align: 'center', valign: 'middle', wrap: true,
          });
          if (s.bodyLines.length) {
            sl.addText(s.bodyLines.map(l => ({ text: `• ${l}`, options: {} })), {
              x: 4, y: 1, w: 5.5, h: 5.5, fontSize: 20, color: t.pptxBody, wrap: true,
            });
          }
        } else if (fam === 'problem') {
          // Problem box
          sl.addText(s.kind === 'you-try' ? 'YOUR TURN' : 'TRY IT', {
            x: 0.5, y: 0.3, w: 3, h: 0.4, fontSize: 11, bold: true, color: t.pptxBody,
          });
          sl.addShape(prs.ShapeType.rect, { x: 0.4, y: 0.8, w: 9.2, h: 3.5, fill: { color: 'FFFFFF' }, line: { color: t.pptxBody + '44', width: 1.5 } });
          sl.addText(s.heading, {
            x: 0.8, y: 0.9, w: 8.4, h: 3.2,
            fontSize: s.heading.length > 60 ? 22 : 28,
            bold: true, color: t.pptxHeading, wrap: true, valign: 'middle',
          });
          if (s.bodyLines.length) {
            sl.addText(s.bodyLines.map(l => ({ text: `• ${l}`, options: {} })), {
              x: 0.5, y: 4.5, w: 9, h: 2.5, fontSize: 18, color: t.pptxBody, wrap: true,
            });
          }
          // Image hint placeholder
          if (s.imageHint && !s.imageUrl) {
            sl.addShape(prs.ShapeType.rect, { x: 6, y: 4.4, w: 3.4, h: 2.8, fill: { color: 'F8F8F8' }, line: { color: 'DDDDDD', width: 1, dashType: 'dash' } });
            sl.addText(`🖼️ ${s.imageHint}`, { x: 6.1, y: 5.3, w: 3.2, h: 1, fontSize: 9, color: 'AAAAAA', align: 'center', wrap: true });
          }
        } else if (fam === 'discuss') {
          // Discuss / prediction
          sl.addText(s.kind === 'prediction' ? 'PREDICT' : 'DISCUSS', {
            x: 0.5, y: 0.3, w: 3, h: 0.4, fontSize: 11, bold: true, color: t.pptxBody,
          });
          sl.addShape(prs.ShapeType.rect, { x: 0.4, y: 0.8, w: 9.2, h: 4, fill: { color: 'FFFFFF' + '10' }, line: { color: t.pptxBody + '44', width: 1.5, dashType: 'dash' } });
          sl.addText(s.heading, {
            x: 0.8, y: 0.9, w: 8.4, h: 3.8,
            fontSize: s.heading.length > 60 ? 22 : 28,
            bold: true, color: t.pptxHeading, wrap: true, valign: 'middle',
          });
          if (s.bodyLines.length) {
            sl.addText(s.bodyLines.map(l => ({ text: `• ${l}`, options: {} })), {
              x: 0.5, y: 5, w: 9, h: 2, fontSize: 17, color: t.pptxBody, wrap: true,
            });
          }
        } else if (fam === 'reveal') {
          // Green-accented answer
          sl.addShape(prs.ShapeType.rect, { x: 0, y: 0, w: 10, h: 0.5, fill: { color: '22C55E' } });
          sl.addText(s.kind === 'exit-ticket' ? 'EXIT TICKET' : 'ANSWER', {
            x: 0.5, y: 0.55, w: 4, h: 0.4, fontSize: 11, bold: true, color: '22C55E',
          });
          sl.addShape(prs.ShapeType.rect, { x: 0.4, y: 1.1, w: 9.2, h: 3.5, fill: { color: 'F0FDF4' }, line: { color: '86EFAC', width: 1.5 } });
          sl.addText(s.heading, {
            x: 0.8, y: 1.2, w: 8.4, h: 3.2,
            fontSize: s.heading.length > 60 ? 22 : 28,
            bold: true, color: t.pptxHeading, wrap: true, valign: 'middle',
          });
          if (s.bodyLines.length) {
            sl.addText(s.bodyLines.map(l => ({ text: `✓ ${l}`, options: {} })), {
              x: 0.5, y: 4.8, w: 9, h: 2.3, fontSize: 18, color: '16A34A', wrap: true,
            });
          }
        } else if (fam === 'alert') {
          // Amber warning
          sl.addShape(prs.ShapeType.rect, { x: 0, y: 0, w: 10, h: 0.5, fill: { color: 'F59E0B' } });
          sl.addText('COMMON MISTAKE', { x: 0.5, y: 0.55, w: 5, h: 0.4, fontSize: 11, bold: true, color: 'D97706' });
          sl.addShape(prs.ShapeType.rect, { x: 0.4, y: 1.1, w: 9.2, h: 3.5, fill: { color: 'FFFBEB' }, line: { color: 'FCD34D', width: 1.5 } });
          sl.addText(s.heading, {
            x: 0.8, y: 1.2, w: 8.4, h: 3.2,
            fontSize: s.heading.length > 60 ? 22 : 28,
            bold: true, color: t.pptxHeading, wrap: true, valign: 'middle',
          });
          if (s.bodyLines.length) {
            sl.addText(s.bodyLines.map(l => ({ text: `• ${l}`, options: {} })), {
              x: 0.5, y: 4.8, w: 9, h: 2.3, fontSize: 18, color: t.pptxBody, wrap: true,
            });
          }
        } else if (fam === 'tip') {
          // Blue teacher note
          sl.addShape(prs.ShapeType.rect, { x: 0, y: 0, w: 0.35, h: 7.5, fill: { color: '3B82F6' } });
          sl.addText('TEACHER NOTE', { x: 0.6, y: 0.3, w: 4, h: 0.4, fontSize: 11, bold: true, color: '3B82F6' });
          sl.addText(s.heading, {
            x: 0.6, y: 0.9, w: 9, h: 2.5,
            fontSize: s.heading.length > 60 ? 22 : 28, bold: true, color: t.pptxHeading, wrap: true,
          });
          if (s.bodyLines.length) {
            sl.addText(s.bodyLines.map(l => ({ text: `• ${l}`, options: {} })), {
              x: 0.6, y: 3.5, w: 9, h: 3.5, fontSize: 18, color: t.pptxBody, wrap: true,
            });
          }
        } else {
          // Standard / concept card
          const kindLabel = SLIDE_KIND_LABELS[s.kind as keyof typeof SLIDE_KIND_LABELS] ?? s.kind;
          sl.addText(kindLabel, { x: 6.5, y: 0.15, w: 3, h: 0.35, fontSize: 9, bold: true, color: t.pptxBody, align: 'right' });
          sl.addText(s.heading, {
            x: 0.5, y: 0.7, w: 9, h: 1.6,
            fontSize: s.heading.length > 40 ? 28 : 36, bold: true, color: t.pptxHeading, wrap: true,
          });
          if (s.subheading) {
            sl.addText(s.subheading, { x: 0.5, y: 2.4, w: 9, h: 0.5, fontSize: 18, color: t.pptxBody, italic: true });
          }
          if (s.bodyLines.length) {
            const bodyY = s.subheading ? 3 : 2.5;
            sl.addText(s.bodyLines.map(l => ({ text: `• ${l}`, options: {} })), {
              x: 0.5, y: bodyY, w: 9, h: 4, fontSize: 20, color: t.pptxBody, wrap: true,
            });
          }
          // Diagram area in PPTX: reserve space with a clean dashed box labeled "Diagram"
          // (SVG cannot be embedded in PPTX; the screen view renders the actual diagram)
          if (s.imageHint && !s.imageUrl) {
            sl.addShape(prs.ShapeType.rect, { x: 0.5, y: 3.2, w: 9, h: 3.4, fill: { color: 'F8FAFB' }, line: { color: 'CCCCCC', width: 1, dashType: 'dash' } });
            sl.addText('[ Diagram ]', { x: 0.5, y: 4.5, w: 9, h: 0.5, fontSize: 11, color: 'BBBBBB', align: 'center', italic: true });
          }
        }

        if (s.speakerNotes) sl.addNotes(s.speakerNotes);
        if (brandingFooter) {
          sl.addText(brandingFooter, { x: 0.3, y: 7.1, w: 9.4, h: 0.3, fontSize: 8, color: t.pptxBody, align: 'left' });
        }

        // ── Bypass watermark: stamped on every slide when certification was not passed ──
        // Cast: WatermarkableSlide is a duck-type test interface; pptxgenjs Slide is compatible at runtime.
        if (isBypass) addPptxBypassWatermark(sl as unknown as WatermarkableSlide);
      }

      // writeFile can silently fail in proxy/iframe environments.
      // Instead: get the binary blob and trigger the download via an anchor element.
      const blob = await prs.write({ outputType: 'blob' }) as Blob;
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${presentation.title.replace(/[^a-z0-9]/gi, '-')}-presentation.pptx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      showToast('PPTX downloaded.', 'success');
    } catch (err) {
      console.error('[PPTX export]', err);
      showToast('PPTX export failed.', 'error');
    }
  }

  async function handleExportPPTX() {
    if (runExportCertification('pptx')) return;
    await doExportPPTX();
  }

  function handlePrint() {
    if (runExportCertification('print')) return;
    window.print();
  }

  // ── Fullscreen chevron helpers ────────────────────────────────────────────
  function fsBack() {
    let p = currentSlide - 1;
    while (p > 0 && hiddenSlides.includes(slides[p].id)) p--;
    goToSlide(Math.max(p, 0));
  }
  function fsForward() {
    // Same logic as keyboard forward
    const curr = slides[currentSlide];
    const isHiddenAnswer = curr?.isAnswerSlide && !revealCurrent && !autoReveal && !teacherOnly;
    if (isHiddenAnswer && afterDiscMode) {
      if (!discussionPhase.has(curr.id)) {
        setDiscussionPhase(prev => new Set([...prev, curr.id]));
      } else {
        setRevealCurrent(true);
      }
    } else {
      let p = currentSlide + 1;
      while (p < slides.length - 1 && hiddenSlides.includes(slides[p].id)) p++;
      goToSlide(Math.min(p, slides.length - 1));
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <ToastContainer toasts={toasts} />
      {regenOpen && (
        <RegenerateDialog onConfirm={handleRegenerate} onCancel={() => setRegenOpen(false)} loading={regenLoading} />
      )}
      {certReport && (
        <CertificationGate
          report={certReport}
          resourceLabel="Presentation"
          onExportAnyway={() => {
            const action = pendingExportAction;
            setCertReport(null);
            setPendingExportAction(null);
            action?.();
          }}
          onClose={() => {
            setCertReport(null);
            setPendingExportAction(null);
          }}
        />
      )}

      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4 print:hidden">
        <button onClick={onBack}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors px-3 py-2 rounded-xl hover:bg-slate-100">
          <ChevronLeft size={15} /> Back to Lesson
        </button>
        <div className="flex-1" />
        <ThemePicker value={themeId} onChange={onThemeChange} compact label="Theme" />
        <Button variant="outline" size="sm"
          onClick={() => setShowBrandingPanel(v => !v)}
          className={`rounded-xl gap-1.5 ${showBrandingPanel || hasBranding(branding) ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600'}`}>
          <Building2 size={14} /> School
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShowNotes(v => !v)}
          className={`rounded-xl gap-1.5 ${showNotes ? 'border-slate-400 bg-slate-100 text-slate-700' : 'border-slate-200 text-slate-600'}`}>
          {showNotes ? <EyeOff size={14} /> : <Eye size={14} />}
          Notes
        </Button>
        <Button variant="outline" size="sm"
          onClick={() => setSlideEditMode(v => !v)}
          className={`rounded-xl gap-1.5 ${slideEditMode ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600'}`}>
          <Sparkles size={14} /> {slideEditMode ? 'Done Editing' : 'Edit Slide'}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setRegenOpen(true)}
          className="rounded-xl border-slate-200 text-slate-600 gap-1.5">
          <RefreshCw size={14} /> Regenerate Slide
        </Button>
        <Button variant="outline" size="sm" onClick={handleSaveClick}
          className={`rounded-xl gap-1.5 ${savedResourceId ? 'border-green-200 bg-green-50 text-green-700' : 'border-slate-200 text-slate-600'}`}>
          {savedResourceId ? <CheckCircle2 size={15} /> : <Save size={15} />}
          {savedResourceId ? 'Saved' : 'Save'}
        </Button>
        <SaveStatusIndicator resourceId={savedResourceId} />
        <div className="relative" ref={exportMenuRef}>
          <Button variant="outline" size="sm" onClick={() => setShowExportMenu(v => !v)}
            className="rounded-xl border-slate-200 text-slate-600 gap-1.5">
            <Download size={14} /> Export
          </Button>
          {showExportMenu && (
            <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-20">
              <button onClick={() => { setShowExportMenu(false); handleExportPPTX(); }}
                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 font-medium">
                📊 PowerPoint (.pptx)
              </button>
              <button onClick={() => { setShowExportMenu(false); handlePrint(); }}
                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 font-medium">
                📄 PDF / Print
              </button>
            </div>
          )}
        </div>
        <Button size="sm" onClick={enterFullscreen}
          className="rounded-xl bg-primary hover:bg-primary/90 text-white gap-1.5">
          <Play size={14} /> Present
        </Button>
        <Button variant="outline" size="sm" onClick={onNew}
          className="rounded-xl border-slate-200 text-slate-600 gap-1.5">
          <RotateCcw size={14} /> New
        </Button>
      </div>

      {/* Branding panel */}
      <BrandingPanel branding={branding} onChange={setBranding} open={showBrandingPanel} onToggle={() => setShowBrandingPanel(v => !v)} />

      {/* Sync warning */}
      {presentation.syncKey && lesson && presentation.syncKey !== lessonContentKey(lesson) && (
        <div className="mb-3 flex items-center gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-800 print:hidden">
          <AlertCircle size={14} className="flex-shrink-0 text-amber-500" />
          <span>The lesson was updated since this presentation was generated. Regenerate to stay in sync.</span>
        </div>
      )}

      {/* Main viewer */}
      <div className="flex gap-4 print:hidden" style={{ height: '600px' }}>

        {/* Slide strip */}
        <div className="w-28 flex-shrink-0 overflow-y-auto space-y-2 pr-1 scroll-smooth" style={{ scrollbarWidth: 'thin' }}>
          {slides.map((s, i) => (
            <SlideThumbnail
              key={s.id} slide={s} index={i} active={i === currentSlide}
              onClick={() => goToSlide(i)} theme={theme}
              isHidden={hiddenSlides.includes(s.id)}
              onToggleHidden={() => setHiddenSlides(prev =>
                prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id]
              )}
            />
          ))}
        </div>

        {/* Main canvas */}
        <div className="flex-1 flex flex-col gap-3 min-w-0">
          <div ref={presenterRef}
            className={`flex-1 rounded-2xl overflow-hidden shadow-lg ${isFullscreen ? 'fixed inset-0 z-50 rounded-none' : ''}`}>
            {isFullscreen ? (
              <div className="w-full h-full relative bg-black flex flex-col">
                {/* Slide */}
                <div className="flex-1 relative overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.div key={currentSlide}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }} className="w-full h-full rounded-none">
                      <SlideCanvas
                        slide={slides[currentSlide]} theme={theme}
                        fullscreen revealHidden={effectiveReveal}
                        afterDiscussion={afterDiscMode}
                        discussionActive={discussionPhase.has(slides[currentSlide]?.id ?? '')}
                      />
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Fullscreen speaker notes panel (S key) */}
                <AnimatePresence>
                  {showFullscreenNotes && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                      className="bg-black/80 border-t border-white/10 px-10 py-4 overflow-hidden">
                      <div className="flex items-start gap-6 max-w-5xl mx-auto">
                        <div className="flex-1">
                          <p className="text-white/40 text-xs font-bold uppercase tracking-widest mb-1">Speaker Notes</p>
                          <p className="text-white/80 text-sm leading-relaxed italic">
                            {slides[currentSlide]?.speakerNotes || 'No notes for this slide.'}
                          </p>
                        </div>
                        {/* Next slide preview */}
                        {(() => {
                          const nextIdx = nextVisible(currentSlide, 1);
                          if (nextIdx !== currentSlide) {
                            return (
                              <div className="flex-shrink-0 border-l border-white/10 pl-6">
                                <p className="text-white/40 text-xs font-bold uppercase tracking-widest mb-1">Up Next</p>
                                <p className="text-white/60 text-sm font-medium truncate max-w-48">
                                  {slides[nextIdx]?.heading}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Fullscreen controls */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-black/70 backdrop-blur-sm rounded-2xl px-6 py-3 z-10">
                  <button onClick={fsBack} className="text-white/70 hover:text-white transition-colors">
                    <ChevronLeft size={24} />
                  </button>
                  {/* Counter shows visible-only position */}
                  <span className="text-white font-semibold text-sm tabular-nums min-w-[3.5rem] text-center">
                    {visiblePos > 0 ? `${visiblePos} / ${visibleTotal}` : '— / —'}
                  </span>
                  <button onClick={fsForward} className="text-white/70 hover:text-white transition-colors">
                    <ChevronRight size={24} />
                  </button>
                  {/* Reveal button for answer slides */}
                  {slides[currentSlide]?.isAnswerSlide && !effectiveReveal && !teacherOnly && (
                    <button
                      onClick={() => {
                        const curr = slides[currentSlide];
                        if (afterDiscMode && !discussionPhase.has(curr.id)) {
                          setDiscussionPhase(prev => new Set([...prev, curr.id]));
                        } else {
                          setRevealCurrent(true);
                        }
                      }}
                      className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 rounded-xl px-4 py-1.5 text-sm text-white font-semibold transition-all">
                      <Eye size={14} /> {afterDiscMode && !discussionPhase.has(slides[currentSlide]?.id ?? '') ? 'Start Discussion' : 'Reveal'}
                    </button>
                  )}
                  {/* S key notes toggle */}
                  <button onClick={() => setShowFullscreenNotes(v => !v)}
                    className={`text-sm font-semibold rounded-xl px-3 py-1.5 transition-all ${showFullscreenNotes ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white/80'}`}>
                    <BookOpen size={14} />
                  </button>
                  <button onClick={exitFullscreen} className="text-white/60 hover:text-white transition-colors">
                    <Minimize2 size={18} />
                  </button>
                </div>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div key={currentSlide}
                  initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.2 }} className="w-full h-full">
                  <SlideCanvas
                    slide={slide} theme={theme}
                    showSpeakerNotes={false}
                    revealHidden={effectiveReveal}
                    afterDiscussion={afterDiscMode}
                    gradeNum={gradeNum}
                    discussionActive={discussionPhase.has(slide?.id ?? '')}
                  />
                </motion.div>
              </AnimatePresence>
            )}
          </div>

          {/* Nav bar */}
          {!isFullscreen && (
            <div className="flex items-center gap-3 px-2">
              <button onClick={fsBack} disabled={currentSlide === 0}
                className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 disabled:opacity-30 transition-colors">
                <ChevronLeft size={18} />
              </button>
              <span className="text-sm font-semibold text-slate-600 tabular-nums flex-1 text-center">
                {visiblePos > 0 ? `${visiblePos} / ${visibleTotal}` : `${currentSlide + 1} / ${total}`}
              </span>
              {slide?.isAnswerSlide && !effectiveReveal && !teacherOnly && (
                <button
                  onClick={() => {
                    if (afterDiscMode && !discussionPhase.has(slide.id)) {
                      setDiscussionPhase(prev => new Set([...prev, slide.id]));
                    } else {
                      setRevealCurrent(true);
                    }
                  }}
                  className="flex items-center gap-1.5 text-xs font-semibold text-primary border border-primary/30 bg-primary/5 hover:bg-primary/10 rounded-xl px-3 py-1.5 transition-all">
                  <Eye size={12} />
                  {afterDiscMode && !discussionPhase.has(slide?.id ?? '') ? 'Start Discussion' : 'Reveal Answer'}
                </button>
              )}
              {slide?.blueprintRef && (
                <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                  🔗 {formatBlueprintRef(slide.blueprintRef)}
                </span>
              )}
              {!slide?.blueprintRef && slide?.teachingMove && (
                <span className="text-xs font-medium text-slate-400 bg-slate-100 rounded-full px-3 py-1">
                  {slide.teachingMove.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              )}
              <button onClick={fsForward} disabled={currentSlide === total - 1}
                className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 disabled:opacity-30 transition-colors">
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Speaker notes (normal view, editable) ──────────────────────────── */}
      {showNotes && slide && (
        <div className="mt-3 bg-slate-50 border border-slate-200 rounded-2xl p-4 print:hidden">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen size={13} className="text-slate-400" />
            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Speaker Notes</p>
          </div>
          <textarea
            value={slide.speakerNotes ?? ''}
            onChange={e => updateCurrentSlide({ speakerNotes: e.target.value })}
            rows={3}
            placeholder="No notes for this slide — click to add…"
            className="w-full text-sm text-slate-700 italic bg-transparent resize-none outline-none leading-relaxed placeholder:text-slate-400"
          />
          {slides[nextVisible(currentSlide, 1)]?.id !== slides[currentSlide]?.id && (
            <p className="mt-2 text-xs text-slate-400">
              <span className="font-semibold not-italic text-slate-500">Up next: </span>
              {slides[nextVisible(currentSlide, 1)]?.heading}
            </p>
          )}
        </div>
      )}

      {/* ── Slide Edit Panel ─────────────────────────────────────────────── */}
      <AnimatePresence>
        {slideEditMode && slide && (
          <motion.div key="slide-edit"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="mt-3 bg-amber-50 border border-amber-200 rounded-2xl p-5 space-y-4 print:hidden">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-amber-800">Editing slide {currentSlide + 1}</p>
              <button onClick={() => setSlideEditMode(false)} className="text-xs text-amber-600 hover:text-amber-900 font-semibold">Close ✕</button>
            </div>

            {/* ── Blueprint sync warning ─────────────────────────────── */}
            {slide.blueprintRef && (
              <div className="flex items-start gap-2.5 bg-white border border-amber-300 rounded-xl px-3 py-2.5">
                <Link size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 leading-snug">
                  <span className="font-bold">Sync contract:</span> This slide is linked to{' '}
                  <span className="font-semibold">{formatBlueprintRef(slide.blueprintRef)}</span> in your lesson blueprint.
                  Editing the heading here breaks the content sync — use{' '}
                  <span className="font-semibold">Update Linked Resources</span> from the lesson plan instead.
                </p>
              </div>
            )}

            {/* Heading */}
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Heading</label>
              <input
                value={slide.heading}
                onChange={e => updateCurrentSlide({ heading: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-amber-200 bg-white text-sm font-semibold text-slate-800 focus:outline-none focus:ring-1 focus:ring-amber-300"
              />
            </div>
            {/* Subheading */}
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Subheading <span className="font-normal text-slate-400">(optional)</span></label>
              <input
                value={slide.subheading ?? ''}
                onChange={e => updateCurrentSlide({ subheading: e.target.value || undefined })}
                className="w-full px-3 py-2 rounded-lg border border-amber-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-amber-300"
              />
            </div>
            {/* Body lines */}
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Bullet points</label>
              <div className="space-y-2">
                {slide.bodyLines.map((line, li) => (
                  <div key={li} className="flex gap-2 items-center">
                    <textarea
                      value={line} onChange={e => updateBodyLine(li, e.target.value)} rows={1}
                      className="flex-1 px-3 py-1.5 rounded-lg border border-amber-200 bg-white text-sm text-slate-700 resize-none focus:outline-none focus:ring-1 focus:ring-amber-300"
                    />
                    <button onClick={() => removeBodyLine(li)} className="text-slate-400 hover:text-red-500 transition-colors text-lg leading-none flex-shrink-0">✕</button>
                  </div>
                ))}
                <button onClick={addBodyLine} className="text-xs text-amber-600 hover:text-amber-800 font-semibold flex items-center gap-1">+ Add line</button>
              </div>
            </div>
            {/* Speaker notes */}
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Speaker notes</label>
              <textarea
                value={slide.speakerNotes ?? ''}
                onChange={e => updateCurrentSlide({ speakerNotes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-amber-200 bg-white text-sm text-slate-600 italic resize-none focus:outline-none focus:ring-1 focus:ring-amber-300"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Print-only: every visible slide on its own landscape page */}
      <div className="hidden print:block">
        <style>{`@media print { @page { size: landscape; margin: 8mm; } }`}</style>
        {slides.filter(s => !hiddenSlides.includes(s.id)).map((s, i) => (
          <div key={s.id} style={{ breakBefore: i > 0 ? 'page' : 'auto', width: '100%', aspectRatio: '16/9', pageBreakBefore: i > 0 ? 'always' : 'auto' }}>
            <SlideCanvas slide={s} theme={theme} fullscreen revealHidden={true} afterDiscussion={false} discussionActive={false} gradeNum={gradeNum} />
          </div>
        ))}
      </div>

      {/* Keyboard hint */}
      <p className="text-xs text-slate-400 text-center mt-3 print:hidden">
        Arrow keys to navigate · <kbd className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">R</kbd> reveal answer ·
        In fullscreen: <kbd className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">S</kbd> toggle notes
      </p>
    </>
  );
}

// ── Props + main component ────────────────────────────────────────────────────
interface PresentationGeneratorProps {
  onBack: () => void;
  onGoMyResources: () => void;
  onNavigateTo: (section: string, ctx?: { fromLessonTitle?: string; fromLessonId?: string }) => void;
  lessonResource?: SavedResource | null;
  initialResource?: SavedResource | null;
}

export function PresentationGenerator({
  onBack, lessonResource, initialResource,
}: PresentationGeneratorProps) {
  const { saveResource, updateResource } = useResources();
  const { toasts, show: showToast } = useToasts();

  const lesson: GeneratedLesson | null = React.useMemo(() => {
    try {
      if (!lessonResource?.lessonData) return null;
      const parsed = JSON.parse(lessonResource.lessonData) as GeneratedLesson;
      // Re-extract blueprint for lessons saved before blueprint extraction existed.
      // Without this, the Presentation server route receives hasBlueprint:false and
      // generates slides that are not anchored to the master lesson content.
      if (!parsed.blueprint) {
        parsed.blueprint = extractBlueprint(parsed);
      }
      return parsed;
    } catch { return null; }
  }, [lessonResource]);

  const savedPresentation: GeneratedPresentation | null = React.useMemo(() => {
    try {
      if (initialResource?.lessonData) return JSON.parse(initialResource.lessonData) as GeneratedPresentation;
      return null;
    } catch { return null; }
  }, [initialResource]);

  const [view, setView] = useState<'config' | 'loading' | 'result'>(savedPresentation ? 'result' : 'config');
  const [config, setConfig] = useState<PresentationConfig>(() =>
    savedPresentation ? savedPresentation.config : defaultPresentationConfig()
  );
  const [presentation, setPresentation] = useState<GeneratedPresentation | null>(savedPresentation);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedResourceId, setSavedResourceId] = useState<string | null>(initialResource?.id ?? null);
  const { savedDefault: presentationDefaultTheme, saveDefault: savePresentationDefault, restoreDefault: restorePresentationDefault } = useDefaultThemes('presentation');
  // Restore teacher-chosen theme from saved presentation; fall back to workspace default.
  const [presentationThemeId, setPresentationThemeId] = useState<TeachBudThemeId>(
    ((savedPresentation as Record<string, unknown> | null)?.themeId as TeachBudThemeId | undefined)
    ?? presentationDefaultTheme,
  );

  // Apply profile theme default once when profile loads (new presentation only)
  const { profile: teacherProfile } = useProfile();
  const presentationProfileDefaultsApplied = useRef(false);
  useEffect(() => {
    if (!teacherProfile || initialResource || presentationProfileDefaultsApplied.current) return;
    presentationProfileDefaultsApplied.current = true;
    if (teacherProfile.defaultPresentationTheme) setPresentationThemeId(teacherProfile.defaultPresentationTheme as TeachBudThemeId);
  }, [teacherProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  const generationIdRef = useRef(0);

  function handleCancelGeneration() {
    generationIdRef.current++;
    setGenerating(false);
    setView('config');
  }

  async function handleGenerate() {
    if (!lesson) return;

    // ── Blueprint Completeness Gate ───────────────────────────────────────────
    // Refuse to generate if the source blueprint is incomplete.
    // An incomplete blueprint produces synchronized emptiness — every resource
    // will render with the same missing content. Catch it here before making
    // any AI call.
    const bpForCheck = lesson.blueprint ?? extractBlueprint(lesson);
    const completenessResult = validateBlueprintCompleteness(bpForCheck);
    if (!completenessResult.complete) {
      const missingList = completenessResult.missingFields
        .map(f => `${f} (${completenessResult.reasons[f]})`)
        .join('; ');
      setError(
        `This lesson's blueprint is incomplete and cannot produce a presentation. ` +
        `Missing content: ${missingList}. ` +
        `Please return to the lesson and regenerate it — the system will rebuild the blueprint with all required content.`
      );
      return;
    }

    const myGenId = ++generationIdRef.current;
    setGenerating(true);
    setView('loading');
    setError(null);
    try {
      const result = await generatePresentationAI(lesson, config);
      if (generationIdRef.current !== myGenId) return;
      setPresentation(result);
      setSavedResourceId(null);
      setView('result');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      if (generationIdRef.current !== myGenId) return;
      if (err instanceof SubscriptionRequiredError) {
        // 402 from server — show the human-readable message and let the teacher navigate to Upgrade
        setError(err.message);
        setView('config');
      } else {
        setError(err instanceof Error ? err.message : 'Generation failed. Please try again.');
        setView('config');
      }
    } finally {
      setGenerating(false);
    }
  }

  // Save merges the current ResultView state (slides, branding, hiddenSlides) back
  function handleSave(updated: GeneratedPresentation) {
    setPresentation(updated); // keep outer state in sync
    const payload = {
      type: 'presentation' as const,
      title: updated.title,
      grade: updated.grade,
      subject: updated.subject,
      topic: lesson?.topic ?? '',
      standard: updated.standard,
      plainText: updated.slides.map((s, i) => `${i + 1}. ${s.heading}`).join('\n'),
      lessonData: JSON.stringify(updated),
      lessonId: lessonResource?.id,
      renderingSpecVersion: REGISTRY_VERSION,
    };
    if (savedResourceId) {
      updateResource(savedResourceId, payload);
      showToast('Presentation saved.');
    } else {
      const id = saveResource(payload);
      setSavedResourceId(id);
      showToast('Presentation saved to My Resources.');
    }
  }

  if (!lesson) {
    return (
      <div className="max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[50vh] text-center gap-5 py-12">
        <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center">
          <Monitor size={30} className="text-amber-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">No lesson selected</h3>
          <p className="text-slate-500 text-sm max-w-sm mx-auto">
            Generate a lesson in the Lesson Planner first, then click <strong>Create Presentation</strong> from the lesson view.
          </p>
        </div>
        <button onClick={onBack}
          className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors flex items-center gap-1">
          <ChevronLeft size={14} /> Go to Lesson Planner
        </button>
      </div>
    );
  }

  return (
    <>
      <ToastContainer toasts={toasts} />
      {view === 'loading' ? (
        <GenerationProgress resourceType="presentation" onCancel={handleCancelGeneration} />
      ) : view === 'config' ? (
        <motion.div key="config" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}>
          <ConfigView
            lesson={lesson} config={config} setConfig={setConfig}
            onGenerate={handleGenerate} generating={generating} onBack={onBack} error={error}
            themeId={presentationThemeId} onThemeChange={setPresentationThemeId}
          />
        </motion.div>
      ) : presentation ? (
        <motion.div key="result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-5xl mx-auto">
          <ResultView
            presentation={presentation} lesson={lesson}
            onBack={onBack}
            onNew={() => { setView('config'); setPresentation(null); setSavedResourceId(null); window.scrollTo({ top: 0 }); }}
            onSave={handleSave}
            savedResourceId={savedResourceId}
            themeId={presentationThemeId}
            onThemeChange={setPresentationThemeId}
          />
        </motion.div>
      ) : null}
    </>
  );
}
